'use client';
import React from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import createCurrencyString from '@repo/ui/src/functions/currency-functions';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useCurrencyString } from '@repo/ui/src/hooks/useCurrencyString';

type VariantType = keyof typeof VARIANT;
type AlignType = keyof typeof ALIGNMENT;

export interface PriceProduct {
  price?: number;
  originalPrice?: number;
  discount?: number;
}

interface Props {
  product: PriceProduct;
  flipOrder?: boolean;
  className?: string;
  strikethroughClassName?: string;
  align?: AlignType;
  color?: 'white';
  variant?: VariantType | false;
  component?: any;
  gutterBottom?: boolean;
  outOfStock?: boolean;
  hideDiscount?: boolean;
  vip?: boolean;
}

const ALIGNMENT = {
  center: 'justify-center text-center',
  left: 'justify-left text-left',
  right: 'justify-right text-right',
};

const VARIANT = {
  h3: 'tra-text-web-heading-3',
  h4: 'tra-text-web-heading-4',
  h5: 'tra-text-web-heading-5',
  h6: 'tra-text-web-heading-6',
  h7: 'tra-text-web-heading-7',
  bodyM: 'tra-text-web-body-m',
  small: 'text-xs',
  sub: 'tra-text-web-sub-1',
  bodyL: 'tra-text-web-body-l',
  bodyXL: 'tra-text-web-body-xl',
};

const ProductPrice = ({
  product,
  flipOrder = false,
  align,
  color,
  variant,
  component: Component = 'p',
  gutterBottom,
  className,
  strikethroughClassName,
  outOfStock,
  hideDiscount,
  vip,
}: Props) => {
  const getCurrencyString = useCurrencyString();
  const { geo } = useCurrentUrl();
  const locale = geo?.locale || 'en-US';
  const currency = geo?.currency || 'USD';
  const { t } = useTranslation();

  if (!product || !product.price || product.price <= 0) return null;

  const hasDiscount =
    product.discount != null
      ? product.discount > 0
      : product.originalPrice != null && product.originalPrice > product.price;

  const displayPrice =
    hideDiscount || !hasDiscount || vip !== false
      ? (product.originalPrice ?? product.price)
      : product.price;

  return (
    <Component
      data-testid="pc-price-component"
      className={classNames(
        'flex gap-x-2',
        color && 'text-white',
        outOfStock && 'text-neutral-300',
        variant ? VARIANT[variant] : 'tra-text-web-body-xl',
        align && ALIGNMENT[align],
        gutterBottom && 'pb-4',
        className,
      )}
    >
      <span className="flex justify-center items-center">
        {!flipOrder && createCurrencyString(locale, currency, displayPrice)}
      </span>
      {!hideDiscount && vip === false && hasDiscount && product.originalPrice && (
        <del
          className={classNames(
            'pr-0 line-through inline',
            strikethroughClassName ?? (color ? 'text-white' : 'text-neutral-300'),
            flipOrder && 'pl-0 pr-2',
            outOfStock && 'text-neutral-300',
            variant ? VARIANT[variant] : 'tra-text-web-body-xl',
          )}
        >
          <span className="sr-only">
            {t('productDetailPageContent.originalPriceText', undefined, {
              default: 'Original Price',
            })}{' '}
            {getCurrencyString(product.originalPrice)}
          </span>
          {getCurrencyString(product.originalPrice)}
        </del>
      )}
      {flipOrder && getCurrencyString(displayPrice ?? product.price)}
      <span className="sr-only">
        {!hideDiscount && product.discount && product.discount > 0
          ? t('productDetailPageContent.salePriceText', undefined, { default: 'Sale Price' })
          : t('productDetailPageContent.priceText', undefined, { default: 'Price' })}{' '}
        {getCurrencyString(displayPrice ?? product.price)}
      </span>
    </Component>
  );
};

export default ProductPrice;
